import React from 'react';
import { ViewState, User } from '../types';
import { Ghost, Gamepad2, Home, User as UserIcon, Newspaper, BookOpen, Globe, Flame } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface NavbarProps {
  currentView: ViewState;
  setView: (view: ViewState) => void;
  user?: User | null;
}

export const Navbar: React.FC<NavbarProps> = ({ currentView, user }) => {
  const { t, i18n } = useTranslation('Navbar');

  const routeMap: Record<ViewState, string> = {
      [ViewState.HOME]: '/',
      [ViewState.NEWS]: '/news',
      [ViewState.MEMES]: '/memes',
      [ViewState.LEADERBOARD]: '/leaderboard',
      [ViewState.GAME]: '/games',
      [ViewState.ORACLE]: '/oracle',
      [ViewState.PROFILE]: '/profile',
      [ViewState.ADMIN]: '/admin',
      [ViewState.RULES]: '/rules',
      [ViewState.CONTACT]: '/contact',
      [ViewState.SKJETAPERSON]: '/skjetaperson',
      [ViewState.CONFESSIONS]: '/confessions',
      [ViewState.WIKI]: '/wiki',
  };

  const navItems = [
    { id: ViewState.HOME, label: t('home'), icon: Home },
    { id: ViewState.NEWS, label: t('news'), icon: Newspaper },
    { id: ViewState.MEMES, label: t('memeArena'), icon: Ghost },
    { id: ViewState.GAME, label: t('games'), icon: Gamepad2 },
    { id: ViewState.WIKI, label: t('about'), icon: BookOpen },
    { id: ViewState.PROFILE, label: t('profile'), icon: UserIcon },
  ];

  const isEnglish = i18n.language.startsWith('en');

  // Language lives in the URL ("/en/..." vs everything else - see
  // index.tsx and i18n.ts), not as an in-place i18n.changeLanguage(), so
  // that Google always sees one deterministic language per URL. Toggling
  // is therefore a real navigation to the equivalent path under the
  // other prefix, not a client-side state change.
  const toggleLanguage = () => {
    const { pathname, search, hash } = window.location;
    const targetPath = isEnglish
      ? pathname.replace(/^\/en(\/|$)/, '/') || '/'
      : `/en${pathname === '/' ? '' : pathname}`;
    window.location.href = targetPath + search + hash;
  };

  return (
    <nav
      className="fixed left-0 bottom-0 w-full bg-skjeta-card/95 backdrop-blur-xl border-t border-white/10 p-2 z-50 md:bottom-auto md:top-0 md:border-b md:border-t-0 md:h-20 flex items-center shadow-2xl"
    >
      <div className="max-w-6xl mx-auto flex justify-around items-center w-full overflow-x-auto no-scrollbar px-2 gap-1 md:gap-4">
        {navItems.map((item) => {
          const isActive = currentView === item.id;
          return (
            <Link
              key={item.id}
              to={routeMap[item.id]}
              onClick={(e) => {
                if (isActive) {
                  e.preventDefault();
                  window.location.reload();
                }
              }}
              className={`flex flex-col items-center p-2 min-w-[60px] rounded-xl transition-all duration-300 group relative focus:outline-none focus:ring-2 focus:ring-skjeta-green ${
                isActive
                  ? 'text-skjeta-green -translate-y-2 md:translate-y-0 md:bg-white/5'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              <item.icon
                className={`w-5 h-5 md:w-6 md:h-6 mb-1 transition-transform group-hover:scale-110 ${isActive ? 'fill-current' : ''}`}
              />
              <span className="text-[9px] md:text-[10px] uppercase font-bold tracking-wider whitespace-nowrap">{item.label}</span>

              {isActive && (
                <span className="absolute -bottom-2 md:bottom-0 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-skjeta-green rounded-full md:hidden"></span>
              )}
            </Link>
          );
        })}

        <button
          onClick={toggleLanguage}
          className="flex flex-col items-center p-2 min-w-[60px] rounded-xl transition-all duration-300 group relative focus:outline-none focus:ring-2 focus:ring-skjeta-green text-gray-400 hover:text-white"
          title={isEnglish ? t('switchToNorwegian') : t('switchToEnglish')}
          aria-label={isEnglish ? t('switchToNorwegian') : t('switchToEnglish')}
        >
          <Globe className="w-5 h-5 md:w-6 md:h-6 mb-1 transition-transform group-hover:scale-110" />
          <span className="text-[9px] md:text-[10px] uppercase font-bold tracking-wider whitespace-nowrap">
            {isEnglish ? 'NO' : 'EN'}
          </span>
        </button>

        {/* Streak Display (if user is logged in) */}
        {user && user.streak > 0 && (
          <div className="hidden md:flex flex-col items-center p-2 min-w-[60px] rounded-xl text-orange-500">
            <div className="relative">
              <Flame className="w-5 h-5 md:w-6 md:h-6 mb-1 animate-pulse" />
            </div>
            <span className="text-[9px] md:text-[10px] uppercase font-bold tracking-wider whitespace-nowrap">
              {user.streak} {t('days')}
            </span>
          </div>
        )}
      </div>
    </nav>
  );
};
